<template>
  <v-navigation-drawer :model-value="view" location="right" :scrim="false"
    :style="view ? { right, width } : { right: '0px', width: '0px' }" 
    elevation="10" temporary>
    <OperationalComponent
      type="counter"
      icon="data_thresholding"
      :title="$t('countersHistoryAlarm')"
      :acceptEnabled="false"
      :denyEnabled="false"
    >
      <v-row>
        <v-col class="text-subtitle-2" cols="3">Transition Name</v-col>
        <v-col class="d-flex align-center">
          <span style="white-space: nowrap; overflow: hidden; max-width: 300px" data-qa="transition-name">
          {{ props.label }}
          </span>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="text-subtitle-2" cols="3">Transition Id</v-col>
        <v-col class="d-flex align-center">
          <span style="white-space: nowrap; overflow: hidden; max-width: 300px" data-qa="transition-id">
          {{ props.transitionId }}
          </span>
        </v-col>
      </v-row>
      <v-row>
        <v-tabs class="main-tabs" v-model="period" align-tabs="title">
          <v-tab :value="TimeSpan.OneWeek">{{$t("counters.oneWeek")}}</v-tab>
          <v-tab :value="TimeSpan.OneMonth">{{$t("counters.oneMonth")}}</v-tab>
          <v-tab :value="TimeSpan.ThreeMonths">{{$t("counters.threeMonths")}}</v-tab>
          <v-tab :value="TimeSpan.SixMonths">{{$t("counters.sixMonths")}}</v-tab>
          <v-tab :value="TimeSpan.OneYear">{{$t("counters.oneYear")}}</v-tab>
        </v-tabs>
      </v-row>
      <v-row>
        <AlarmHistoryCounterGraph :graphData="modelValue.content"></AlarmHistoryCounterGraph>
      </v-row>
      <v-row>
        <corvina-table
          class="custom-corvina-table"
          id="alarm-table"
          :headers="counterHeaders"
          :items="modelValue.content"
          :itemsPerPage="modelValue.size"
          :currentPage="modelValue.number"
          :itemsLength="modelValue.totalElements"
          @currentPageChanged="currentPageChanged"
          @currentItemsPerPageChanged="currentItemsPerPageChanged"
          :isExpandable="false">
        </corvina-table>
      </v-row>
    </OperationalComponent>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { dataStore } from '../stores/data'
import { useDisplay } from 'vuetify'
import OperationalComponent from "./UIComponents/OperationalComponent.vue";
import { CORVINA } from '@corvina/vue-components-library';
import { debounce } from 'lodash';
import { emptyPage } from '../interfaces/IPage';
import { ICountersFilter } from '../interfaces/IAlarm';
import { CountersOrderBy, SortDTO } from '../utils/type.util';
import { useI18n } from 'vue-i18n';
import AlarmHistoryCounterGraph from './AlarmHistoryCounterGraph.vue';

const { t, d, locale } = useI18n();

let debouncedRefresh = null as any;

const props = defineProps<{
  view: boolean;
  alarmId: string;
  transitionId: string;
  label: string;
}>();

const noDataForGraph = ref(true);

const modelValue = ref({
  ...emptyPage,
  filters: {
    orderBy: CountersOrderBy.timestamp,
    orderDir: SortDTO.DESC,
  } as ICountersFilter,
});

enum TimeSpan {
  OneWeek = "WEEK",
  OneMonth = "MONTH",
  ThreeMonths = "THREE_MONTHS",
  SixMonths = "SIX_MONTHS",
  OneYear = "YEAR"
}

const counterHeaders = ref([
  {
    key: "timestamp",
    title: t("counters.timestamp"),
    tooltip: t("counters.timestamp"),
    align: "left",
    id: "name",
    sortable: true,
    type: CORVINA.TABLE.CELL_TYPE.STRING,
    propString: "timestamp",
  },
  {
    key: "value",
    title: t("counters.value"),
    tooltip: t("counters.value"),
    align: "center",
    id: "value",
    sortable: false,
    type: CORVINA.TABLE.CELL_TYPE.STRING,
    propString: "value",
  },
]);

const currentPageChanged = async (newVal: number) => {
  modelValue.value.number = newVal;
  await refresh();
};

const currentItemsPerPageChanged = async (newVal: number) => {
  modelValue.value.size = newVal;
  await refresh();
};

const format = (value) => {
  if (value != "" && value != null) {
    return d(new Date(value), 'dateHour', locale.value);
  }
};

const fetchCounters = async () => {
  const counters = await data.fetchCounters({
    filters: modelValue.value.filters,
    alarmId: props.alarmId,
    transitionId: props.transitionId,
    period: period.value,
    page: (modelValue.value.number || 1) - 1,
    pageSize: modelValue.value.size,
  });

  if (counters) {
    noDataForGraph.value = false;
    modelValue.value.totalElements = counters.totalElements;
    modelValue.value.content = counters.content.map((c) => {
      return {
        value: c.counter,
        timestamp: format(c.time),
      }
    });
  } else {
    noDataForGraph.value = true;
  }
};

const refresh = async () => {
  if (debouncedRefresh == null)
    debouncedRefresh = debounce(fetchCounters, 200);
  await debouncedRefresh();
};

const data = dataStore();

const nameDisplay = useDisplay().name;

const width = ref("0px");
const right = ref("0px");
const period = ref(TimeSpan.OneWeek);

watch(() => props.view, async (newVal) => {
  if (newVal) {
    await refresh();
    switch (nameDisplay.value) {
      case "xs":
        width.value = "100%";
        right.value = "100%";
      case "sm":
        width.value = "50%";
        right.value = "50%";
      case 'md': 
        width.value = "50%";
        right.value = "50%";
      default:
        width.value = "700px";
        right.value = "600px";
    }
  } else {
    width.value = "0px";
    right.value = "0px";
  }
});

watch(() => period.value, async (newVal: TimeSpan) => {
  await refresh();
});

watch(() => props.transitionId, async (newVal: string) => {
  await refresh();
});

watch(() => props.alarmId, async (newVal: string) => {
  await refresh();
});
</script>


<style scoped>
.custom-corvina-table {
  width: 100%;
}
</style>
